import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import { useParams, useHistory } from 'react-router';
import Api from '../../util/api';
import Loading from '../../shared/components/custom/Loading';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import t, { partial } from '../../util/translation/translation';
import NewsProviderForm from './components/NewsProviderForm';

const s = partial('shared');
const NewsProviderFormPage = () => {
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState(false);
  const { providerID } = useParams();
  // Without an id: adding a new feed
  const isCreate = !providerID;
  // The club's feeds, to warn about adding one twice
  const [allProviders, setAllProviders] = useState([]);
  const [newsCategories, setNewsCategories] = useState([]);
  const [providerData, setProviderData] = useState(null);
  // The club's notification labels; null if they couldn't be loaded (then
  // the provider's label is left as it is)
  const [notificationLabels, setNotificationLabels] = useState(null);
  const history = useHistory();
  const onCreate = async (values) => {
    const url = values.url.trim();
    if (
      allProviders.some(
        (item) =>
          item.type === values.type.value &&
          `${item.config?.url || ''}`.replace(/\/$/, '') ===
            url.replace(/\/$/, '')
      )
    ) {
      toast.error(t('NewsForm.providerExists'));
      return;
    }
    setLoading(true);
    try {
      await Api.news.createProvider({
        type: values.type.value,
        url,
        categoryId: values.category?.value || undefined,
        pushEnabled: !!values.pushEnabled,
        ...(notificationLabels && {
          notificationLabelId: values.notificationLabel?.value || null,
        }),
      });
      setLoading(false);
      toast.success(t('NewsForm.providerAdded'));
      history.replace('/news?tab=2');
    } catch (err) {
      setLoading(false);
      toast.error(
        typeof err === 'string' && err ? err : t('NewsForm.providerAddFailed')
      );
    }
  };
  const onSubmit = async (values) => {
    if (isCreate) {
      await onCreate(values);
      return;
    }
    setLoading(true);
    const payload = {
      categoryId: values.category.value,
      pushEnabled: values.pushEnabled,
      // Members who switched the label off don't get the provider's pushes
      ...(notificationLabels && {
        notificationLabelId: values.notificationLabel?.value || null,
      }),
    };
    try {
      await Api.news.updateProviderCategory(providerID, payload);
      setLoading(false);
      history.replace('/news?tab=2');
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoading(false);
    }
  };
  const [deleteVisible, setDeleteVisible] = useState(false);
  const onDelete = async () => {
    setLoading(true);
    try {
      await Api.news.deleteProvider(providerID);
      toast.success(t('NewsForm.providerDeleted'));
      history.replace('/news?tab=2');
    } catch (err) {
      setLoading(false);
      setDeleteVisible(false);
      toast.error(
        typeof err === 'string' && err ? err : s('somethingWentWrong')
      );
    }
  };
  const getNewsCategories = async () => {
    setLoadingCategories(true);
    try {
      const categoryList = await Api.news.getNewsCategories();
      setNewsCategories(categoryList);
      setLoadingCategories(false);
    } catch (err) {
      setLoadingCategories(false);
      toast.error(err || t('NewsFormPage.fetchingNewsFailed'));
    }
  };
  const getProviderData = async () => {
    setLoadingProvider(true);
    try {
      const providers = await Api.news.getNewsProviders();
      setAllProviders(providers || []);
      const foundProvider = isCreate
        ? null
        : providers.find((item) => item.id === providerID);
      setProviderData(foundProvider);
      setLoadingProvider(false);
    } catch (err) {
      setLoadingProvider(false);
      toast.error(err || t('NewsFormPage.fetchingNewsFailed'));
    }
  };
  const getNotificationLabels = async () => {
    try {
      const labels = await Api.userDevices.getAllNotificationLabels();
      setNotificationLabels(labels || []);
    } catch (err) {
      setNotificationLabels(null);
    }
  };
  useEffect(() => {
    getNewsCategories();
    getNotificationLabels();
    // Back from creating a label in another tab: show it in the list
    const onFocus = () => getNotificationLabels();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  useEffect(() => {
    getProviderData();
    // getProviderData is redefined every render; depending on it would
    // refetch on every render instead of only when providerID changes.
    // eslint-disable-next-line
  }, [providerID]);
  return (
    <Container>
      <Loading loading={loading || loadingCategories || loadingProvider} />
      <DeleteModal
        visible={deleteVisible}
        type={t('NewsForm.feed').toLowerCase()}
        handleModal={setDeleteVisible}
        modalAction={onDelete}
      >
        <p className="delete-modal__warning">
          {t('NewsForm.deleteProviderWarning')}
        </p>
      </DeleteModal>
      <Row>
        <Col md={12}>
          <h3 className="page-title">
            {isCreate ? t('NewsForm.addProvider') : t('NewsForm.editProvider')}
          </h3>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Card>
            <CardBody>
              <NewsProviderForm
                onSubmit={onSubmit}
                categories={newsCategories}
                providerData={providerData}
                notificationLabels={notificationLabels}
                isCreate={isCreate}
                onDelete={() => setDeleteVisible(true)}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default NewsProviderFormPage;
